import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../../utils/axiosConfig';

// ── Types ────────────────────────────────────────────────────────────────────

export interface CPProductClaim {
  id: string;
  product_id: string;
  claim_type: string;
  claim_value: string;
}

export interface CPProduct {
  id: string;
  customer_id: string;
  brand_id: string | null;
  product_group_id: string | null;
  product_name: string;
  local_product_name: string | null;
  sub_brand: string | null;
  industry: string | null;
  marketing_category: string | null;
  product_category: string | null;
  product_application: string | null;
  serving_temperature: string | null;
  plant_based_category: string | null;
  category: string | null;
  subcategory: string | null;
  format: string | null;
  packaging_type: string | null;
  size_of_pack: string | null;
  region: string | null;
  country: string | null;
  description: string | null;
  positioning: string | null;
  flavors: string | null;
  ingredients: string | null;
  nutrition_info: string | null;
  image_url: string | null;
  product_url: string | null;
  is_fruit_preparation_likely: boolean;
  agrana_present: boolean;
  agrana_recipe_code: string | null;
  agrana_volume_tons: number | null;
  agrana_plant: string | null;
  launch_date: string | null;
  price_positioning: string | null;
  price_of_selling_unit: string | null;
  data_source: string;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
  claims: CPProductClaim[];
}

export interface CPCustomer {
  id: string;
  name: string;
  customer_type: string | null;
  notes: string | null;
  created_at: string;
}

export interface CPBrand {
  id: string;
  customer_id: string;
  name: string;
  created_at: string;
}

export interface CPCustomerWithBrands extends CPCustomer {
  brands: CPBrand[];
}

export interface CPReferenceData {
  regions: string[];
  countries: string[];
  industries: string[];
  marketing_categories: Record<string, string[]>;
  product_categories: string[];
  formats: string[];
  positioning: string[];
  product_applications: string[];
  claims: string[];
  features: string[];
  serving_temperatures: string[];
  plant_based_categories: string[];
}

export interface CPProductListResponse {
  items: CPProduct[];
  total: number;
  offset: number;
  limit: number;
}

export interface CPImportResult {
  rows_imported: number;
  rows_updated: number;
  rows_failed: number;
  errors: Array<{ row: number; error: string }>;
  import_log_id: string;
}

export interface CPRunHistoryItem {
  id: string;
  user_id: string | null;
  customer_id: string | null;
  customer_name: string | null;
  label: string | null;
  filters_json: Record<string, any>;
  product_count: number;
  created_at: string;
}

export interface CPGenerationJob {
  id: string;
  user_id: string | null;
  external_job_id: string;
  company: string;
  params_json: Record<string, any>;
  status: string; // accepted | pending | running | completed | failed | cancelled
  progress: string | null;
  products_found: number;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface CPGenerationJobCreate {
  company: string;
  depth: number;
  regions: string[];
  countries: string[];
  industry: string | null;
  marketing_categories: string[];
  product_category: string | null;
  format: string[];
  positioning: string[];
  product_application: string[];
  claims_filter: string[];
  features_filter: string[];
  serving_temperature: string[];
  plant_based_categories: string[];
}

export interface CPFilters {
  customer_id: string | null;
  generation_job_id: string | null;
  region: string;
  country: string;
  industry: string;
  marketing_category: string;
  product_category: string;
  product_application: string;
  serving_temperature: string;
  plant_based_category: string;
  category: string;
  subcategory: string;
  positioning: string;
  agrana_present: string; // "all" | "true" | "false"
  claims: string[];
  search: string;
}

// ── State ────────────────────────────────────────────────────────────────────

interface CustomerProfilingState {
  customers: CPCustomer[];
  selectedCustomer: CPCustomerWithBrands | null;
  products: CPProduct[];
  totalProducts: number;
  referenceData: CPReferenceData | null;
  filters: CPFilters;
  offset: number;
  limit: number;
  loading: boolean;
  customersLoading: boolean;
  importResult: CPImportResult | null;
  importing: boolean;
  error: string | null;
  runHistory: CPRunHistoryItem[];
  runHistoryLoading: boolean;
  generationJobs: CPGenerationJob[];
  generationJobsLoading: boolean;
  submittingJob: boolean;
}

const initialFilters: CPFilters = {
  customer_id: null,
  generation_job_id: null,
  region: '',
  country: '',
  industry: '',
  marketing_category: '',
  product_category: '',
  product_application: '',
  serving_temperature: '',
  plant_based_category: '',
  category: '',
  subcategory: '',
  positioning: '',
  agrana_present: 'all',
  claims: [],
  search: '',
};

const initialState: CustomerProfilingState = {
  customers: [],
  selectedCustomer: null,
  products: [],
  totalProducts: 0,
  referenceData: null,
  filters: initialFilters,
  offset: 0,
  limit: 100,
  loading: false,
  customersLoading: false,
  importResult: null,
  importing: false,
  error: null,
  runHistory: [],
  runHistoryLoading: false,
  generationJobs: [],
  generationJobsLoading: false,
  submittingJob: false,
};

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchCustomers = createAsyncThunk(
  'customerProfiling/fetchCustomers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/api/cp/customers');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch customers');
    }
  }
);

export const fetchCustomerWithBrands = createAsyncThunk(
  'customerProfiling/fetchCustomerWithBrands',
  async (customerId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/api/cp/customers/${customerId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch customer');
    }
  }
);

export const fetchReferenceData = createAsyncThunk(
  'customerProfiling/fetchReferenceData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/api/cp/reference');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch reference data');
    }
  }
);

export const fetchProducts = createAsyncThunk(
  'customerProfiling/fetchProducts',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { customerProfiling: CustomerProfilingState };
      const { filters, offset, limit } = state.customerProfiling;

      const params: Record<string, any> = { offset, limit };

      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.generation_job_id) params.generation_job_id = filters.generation_job_id;
      if (filters.region) params.region = filters.region;
      if (filters.country) params.country = filters.country;
      if (filters.industry) params.industry = filters.industry;
      if (filters.marketing_category) params.marketing_category = filters.marketing_category;
      if (filters.product_category) params.product_category = filters.product_category;
      if (filters.product_application) params.product_application = filters.product_application;
      if (filters.serving_temperature) params.serving_temperature = filters.serving_temperature;
      if (filters.plant_based_category) params.plant_based_category = filters.plant_based_category;
      if (filters.category) params.category = filters.category;
      if (filters.subcategory) params.subcategory = filters.subcategory;
      if (filters.positioning) params.positioning = filters.positioning;
      if (filters.agrana_present !== 'all') params.agrana_present = filters.agrana_present === 'true';
      if (filters.search) params.search = filters.search;

      // Claims filter (multiple values)
      const claimsParams = filters.claims.length > 0
        ? filters.claims.map(c => `claims=${encodeURIComponent(c)}`).join('&')
        : '';

      const queryString = new URLSearchParams(params).toString();
      const url = `/api/cp/products?${queryString}${claimsParams ? '&' + claimsParams : ''}`;

      const response = await apiClient.get(url);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch products');
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customerProfiling/createCustomer',
  async (data: { name: string; customer_type?: string; notes?: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/api/cp/customers', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create customer');
    }
  }
);

export const importFile = createAsyncThunk(
  'customerProfiling/importFile',
  async (
    { file, sourceType, customerId }: { file: File; sourceType: string; customerId?: string },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source_type', sourceType);
      if (customerId) formData.append('customer_id', customerId);

      const response = await apiClient.post('/api/cp/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Import failed');
    }
  }
);

export const fetchRunHistory = createAsyncThunk(
  'customerProfiling/fetchRunHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/api/cp/run-history');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch run history');
    }
  }
);

export const saveRunHistory = createAsyncThunk(
  'customerProfiling/saveRunHistory',
  async (
    data: {
      customer_id?: string | null;
      customer_name?: string | null;
      label?: string | null;
      filters_json: Record<string, any>;
      product_count: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.post('/api/cp/run-history', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to save run');
    }
  }
);

export const deleteRunHistory = createAsyncThunk(
  'customerProfiling/deleteRunHistory',
  async (runId: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/api/cp/run-history/${runId}`);
      return runId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete run');
    }
  }
);

export const updateRunHistory = createAsyncThunk(
  'customerProfiling/updateRunHistory',
  async (
    { runId, product_count }: { runId: string; product_count: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.patch(`/api/cp/run-history/${runId}`, { product_count });
      return response.data as CPRunHistoryItem;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update run');
    }
  }
);

export const submitGenerationJob = createAsyncThunk(
  'customerProfiling/submitGenerationJob',
  async (payload: CPGenerationJobCreate, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/api/cp/generate', payload);
      return response.data as CPGenerationJob;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to submit job');
    }
  }
);

export const fetchGenerationJobs = createAsyncThunk(
  'customerProfiling/fetchGenerationJobs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/api/cp/generate/jobs');
      return response.data as CPGenerationJob[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch jobs');
    }
  }
);

export const pollGenerationJob = createAsyncThunk(
  'customerProfiling/pollGenerationJob',
  async (jobId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/api/cp/generate/jobs/${jobId}`);
      return response.data as CPGenerationJob;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to poll job');
    }
  }
);

export const cancelGenerationJob = createAsyncThunk(
  'customerProfiling/cancelGenerationJob',
  async (jobId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/api/cp/generate/jobs/${jobId}`);
      return response.data as CPGenerationJob;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to cancel job');
    }
  }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const customerProfilingSlice = createSlice({
  name: 'customerProfiling',
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<Partial<CPFilters>>) => {
      // If any filter OTHER than generation_job_id is being set, clear generation_job_id
      // (prevents stale job filter when user manually changes dropdowns)
      const keys = Object.keys(action.payload);
      const isOnlyJobFilter = keys.length === 1 && keys[0] === 'generation_job_id';
      const includesJobFilter = 'generation_job_id' in action.payload;
      if (!isOnlyJobFilter && !includesJobFilter && state.filters.generation_job_id) {
        state.filters.generation_job_id = null;
      }
      state.filters = { ...state.filters, ...action.payload };
      state.offset = 0; // Reset pagination on filter change
    },
    resetFilters: (state) => {
      state.filters = { ...initialFilters, customer_id: state.filters.customer_id };
      state.offset = 0;
    },
    setOffset: (state, action: PayloadAction<number>) => {
      state.offset = action.payload;
    },
    clearImportResult: (state) => {
      state.importResult = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Customers
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.customersLoading = true;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.customersLoading = false;
        state.customers = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.customersLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Customer with Brands
    builder
      .addCase(fetchCustomerWithBrands.fulfilled, (state, action) => {
        state.selectedCustomer = action.payload;
      });

    // Fetch Reference Data
    builder
      .addCase(fetchReferenceData.fulfilled, (state, action) => {
        state.referenceData = action.payload;
      });

    // Fetch Products
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.items;
        state.totalProducts = action.payload.total;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create Customer
    builder
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.customers.push(action.payload);
      });

    // Import File
    builder
      .addCase(importFile.pending, (state) => {
        state.importing = true;
        state.importResult = null;
        state.error = null;
      })
      .addCase(importFile.fulfilled, (state, action) => {
        state.importing = false;
        state.importResult = action.payload;
      })
      .addCase(importFile.rejected, (state, action) => {
        state.importing = false;
        state.error = action.payload as string;
      });

    // Run History
    builder
      .addCase(fetchRunHistory.pending, (state) => {
        state.runHistoryLoading = true;
      })
      .addCase(fetchRunHistory.fulfilled, (state, action) => {
        state.runHistoryLoading = false;
        state.runHistory = action.payload;
      })
      .addCase(fetchRunHistory.rejected, (state) => {
        state.runHistoryLoading = false;
      });

    builder
      .addCase(saveRunHistory.fulfilled, (state, action) => {
        state.runHistory = [action.payload, ...state.runHistory];
      });

    builder
      .addCase(deleteRunHistory.fulfilled, (state, action) => {
        state.runHistory = state.runHistory.filter(r => r.id !== action.payload);
      })
      .addCase(updateRunHistory.fulfilled, (state, action) => {
        const idx = state.runHistory.findIndex(r => r.id === action.payload.id);
        if (idx >= 0) state.runHistory[idx] = action.payload;
      });

    // Generation Jobs
    builder
      .addCase(submitGenerationJob.pending, (state) => {
        state.submittingJob = true;
        state.error = null;
      })
      .addCase(submitGenerationJob.fulfilled, (state, action) => {
        state.submittingJob = false;
        state.generationJobs = [action.payload, ...state.generationJobs];
      })
      .addCase(submitGenerationJob.rejected, (state, action) => {
        state.submittingJob = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchGenerationJobs.pending, (state) => {
        state.generationJobsLoading = true;
      })
      .addCase(fetchGenerationJobs.fulfilled, (state, action) => {
        state.generationJobsLoading = false;
        state.generationJobs = action.payload;
      })
      .addCase(fetchGenerationJobs.rejected, (state) => {
        state.generationJobsLoading = false;
      });

    builder
      .addCase(pollGenerationJob.fulfilled, (state, action) => {
        const idx = state.generationJobs.findIndex(j => j.id === action.payload.id);
        if (idx !== -1) state.generationJobs[idx] = action.payload;
      })
      .addCase(cancelGenerationJob.fulfilled, (state, action) => {
        const idx = state.generationJobs.findIndex(j => j.id === action.payload.id);
        if (idx !== -1) state.generationJobs[idx] = action.payload;
      });
  },
});

export const { setFilter, resetFilters, setOffset, clearImportResult, clearError } =
  customerProfilingSlice.actions;
export default customerProfilingSlice.reducer;

